import { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BaseBarChart } from "@/components/Charts/BaseBarChart";
import { TargetCostData } from "@/types/target-cost";
import { formatCurrency } from "@/utils/format";
import { BarChart3 } from "lucide-react";

interface CostComparisonChartProps {
  data: TargetCostData;
}

export const CostComparisonChart = memo(function CostComparisonChart({ data }: CostComparisonChartProps) {
  const { totalPrices, finalPWDD } = data.costs;

  if (!totalPrices || !finalPWDD) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Target Cost vs Final PWDD</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
            <BarChart3 className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-center">
              Enter Total Prices and Final PWDD to see the chart.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const variance = totalPrices - finalPWDD;
  const isOverrun = variance < 0;

  const chartData = [{
    name: "",  // Remove redundant name since we only have one group
    "Target Cost": totalPrices,
    "Final PWDD": finalPWDD,
  }];

  const chartConfig = {
    margin: { top: 20, right: 30, bottom: 50, left: 70 },
    xAxis: {
      angle: 0,
      textAnchor: "middle",
      interval: 0,
      fontSize: 12,
    },
    yAxis: {
      width: 60,
      tickFormatter: (value: number) => `£${(value / 1000).toFixed(0)}k`,
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          Target Cost vs Final PWDD
          <span className={`text-sm font-normal ${isOverrun ? 'text-destructive' : 'text-green-600'}`}>
            Variance: {formatCurrency(Math.abs(variance))} {isOverrun ? '(Over)' : '(Under)'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <BaseBarChart
            data={chartData}
            dataKeys={["Target Cost", "Final PWDD"]}
            colors={[
              'hsl(var(--chart-1))', // Blue for Target Cost
              isOverrun ? 'hsl(var(--destructive))' : 'hsl(var(--chart-2))' // Red/Green for Final PWDD
            ]}
            tooltipFormatter={(value: number) => formatCurrency(value)}
            margin={chartConfig.margin}
            xAxis={chartConfig.xAxis}
            yAxis={chartConfig.yAxis}
          />
        </div>
      </CardContent>
    </Card>
  );
});